import React, { useRef, useState } from "react";
import {
  ConfigProvider,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Tooltip,
  Switch,
  Empty,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  Check2,
  PencilSquare,
  Wallet,
  XLg,
} from "react-bootstrap-icons";
import Style from "./SupplierTable.module.scss";

const SupplierTable = ({ showAddress, data, onToggleOpen, updatingOpenId }) => {
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters?.();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 10 }} dir="rtl">
        <Input
          ref={searchInput}
          placeholder={`جست‌وجوی ${title}...`}
          value={selectedKeys[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 10, display: "block" }}
        />
        <Space wrap>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            فیلتر
          </Button>
          <Button
            onClick={() => {
              handleReset(clearFilters);
              confirm();
            }}
            size="small"
          >
            پاک کردن
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            بستن
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#c20c64" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(String(value).toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text || "—"
      ),
  });

  const columns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
      width: 90,
      align: "center",
      sorter: (a, b) => Number(a.id) - Number(b.id),
      ...getColumnSearchProps("id", "شناسه"),
      render: (text) => <span className={Style.numText}>{text}</span>,
    },
    {
      title: "نام مغازه / فروشگاه",
      dataIndex: "marketName",
      key: "marketName",
      width: 170,
      ...getColumnSearchProps("marketName", "نام مغازه"),
      render: (text, record) => (
        <Tooltip title="مشاهده جزئیات تأمین‌کننده">
          <p
            className={Style.clickableName}
            onClick={() => showAddress(4, record)}
          >
            {text || "بدون نام"}
          </p>
        </Tooltip>
      ),
    },
    {
      title: "مسئول",
      dataIndex: "fName",
      key: "fName",
      width: 160,
      ...getColumnSearchProps("fName", "نام مسئول"),
      render: (text, record) => (
        <Tooltip title="مشاهده جزئیات تأمین‌کننده">
          <p
            className={Style.clickableName}
            onClick={() => showAddress(4, record)}
          >
            {text || "—"}
          </p>
        </Tooltip>
      ),
    },
    {
      title: "شماره تلفن",
      dataIndex: "phone",
      key: "phone",
      width: 130,
      ...getColumnSearchProps("phone", "شماره تلفن"),
      render: (text) => <span className={Style.numText}>{text || "—"}</span>,
    },
    {
      title: "نوع کسب‌وکار",
      dataIndex: "type",
      key: "type",
      width: 120,
      align: "center",
      filters: [
        { text: "رستوران", value: "رستوران" },
        { text: "فروشگاه", value: "فروشگاه" },
      ],
      onFilter: (value, record) =>
        record.type?.trim().includes(value.trim()),
      render: (text, record) => (
        <Tag color={record.typeUrl === "food" ? "orange" : "blue"}>
          {text}
        </Tag>
      ),
    },
    {
      title: "تعداد کالا",
      dataIndex: "countProduct",
      key: "countProduct",
      width: 110,
      align: "center",
      sorter: (a, b) => Number(a.countProduct || 0) - Number(b.countProduct || 0),
      render: (text) => (
        <span className={Style.numText}>{text != null ? text : "—"}</span>
      ),
    },
    {
      title: "مجموع سفارش",
      dataIndex: "totalOrder",
      key: "totalOrder",
      width: 120,
      align: "center",
      sorter: (a, b) => Number(a.totalOrder || 0) - Number(b.totalOrder || 0),
      render: (text) => (
        <span className={Style.numText}>{text != null ? text : "—"}</span>
      ),
    },
    {
      title: "موجودی حساب",
      dataIndex: "money",
      key: "money",
      width: 130,
      align: "center",
      render: (text) => (
        <span className={Style.moneyText}>{text || "۰"}</span>
      ),
    },
    {
      title: "وضعیت کاربر",
      dataIndex: "status",
      key: "status",
      width: 110,
      align: "center",
      filters: [
        { text: "فعال", value: "1" },
        { text: "غیرفعال", value: "0" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag color={status === "1" ? "success" : "error"}>
          {status === "1" ? "فعال" : "غیرفعال"}
        </Tag>
      ),
    },
    {
      title: "عملیات",
      dataIndex: "action",
      key: "action",
      width: 260,
      align: "center",
      render: (_, record) => {
        const isStoreOpen = record.isOpen;
        const isUpdating = updatingOpenId === record.id;

        return (
          <div className={Style.btnHolder}>
            {/* New Store Open/Close Switch */}
            <Tooltip
              title={
                isStoreOpen
                  ? "فروشگاه باز است (برای بستن کلیک کنید)"
                  : "فروشگاه بسته است (برای باز کردن کلیک کنید)"
              }
            >
              <Switch
                checked={isStoreOpen}
                checkedChildren="باز"
                unCheckedChildren="بسته"
                loading={isUpdating}
                onChange={(checked) =>
                  onToggleOpen && onToggleOpen(record, checked ? 1 : 0)
                }
                className={Style.openStoreSwitch}
              />
            </Tooltip>

            {/* Edit Button */}
            <Tooltip title="ویرایش اطلاعات تأمین‌کننده">
              <button
                onClick={() => showAddress(2, record)}
                className={Style.editBtn}
                aria-label="ویرایش"
              >
                <PencilSquare />
              </button>
            </Tooltip>

            {/* Shaba Button */}
            <Tooltip title="اطلاعات حساب و شبا">
              <button
                onClick={() => showAddress(5, record)}
                className={Style.shabaBtn}
                aria-label="شبا"
              >
                شبا
              </button>
            </Tooltip>

            {/* Handle Money Button */}
            <Tooltip title="افزایش / کسر موجودی (تشویق / جریمه)">
              <button
                onClick={() => showAddress(9, record)}
                className={Style.moneyBtn}
                aria-label="تشویق و جریمه"
              >
                +پول
              </button>
            </Tooltip>

            {/* Wallet Transactions Button */}
            <Tooltip title="تراکنش‌های کیف پول">
              <button
                onClick={() => showAddress(1, record)}
                className={Style.walletBtn}
                aria-label="کیف پول"
              >
                <Wallet />
              </button>
            </Tooltip>

            {/* Activate / Deactivate Toggle Button */}
            <Tooltip
              title={
                record.status === "1"
                  ? "غیرفعال‌سازی کاربر"
                  : "فعال‌سازی مجدد کاربر"
              }
            >
              <button
                onClick={() => showAddress(3, record)}
                className={record.status === "1" ? Style.deleteBtn : Style.activeBtn}
                aria-label="تغییر وضعیت فعال‌سازی"
              >
                {record.status === "1" ? <XLg /> : <Check2 />}
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <ConfigProvider direction="rtl">
      <div className={Style.tableWrapper} dir="rtl">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          locale={{
            emptyText: <Empty description="هیچ تأمین‌کننده‌ای یافت نشد." />,
            filterConfirm: "فیلتر",
            filterReset: "پاک کردن",
            filterEmptyText: "موردی یافت نشد",
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            position: "bottomCenter",
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total) => `تعداد کل: ${total} تأمین‌کننده`,
          }}
          scroll={{ x: 1650 }}
          size="middle"
        />
      </div>
    </ConfigProvider>
  );
};

export default SupplierTable;
